/**
 * The stack builder's offer block model: what the screen loads from `offerblocks/{id}` and what it
 * sends back. The requests match the classic editor's (views/OfferBlocks/OfferBlockDetails), so a
 * block saved here opens unchanged there. Nothing here imports from the classic folders, so they can
 * be deleted once this editor has replaced them.
 */
import { handleCriteriaSubmit, normalizeCriteriaConfiguration } from '../../../utils/CriteriaHelpers';

/* eslint-disable @typescript-eslint/no-explicit-any -- these mirror loosely-typed API entities */
export type Ref = { id: number; name?: string; [key: string]: any };
export type Offer = {
  id: number;
  name: string;
  isActive?: boolean;
  offerType?: { id: number; name: string } | null;
  advertiser?: { id: number; name: string } | null;
  [key: string]: any;
};
export type BlockItem = { key: string; offer: Offer; layoutOverride: Ref | null; containerOverride: Ref | null };

/** Return URL, Landing Page, Offer Block, Dynamic Offer Section and Destination offers never go into a block. */
export const EXCLUDED_OFFER_TYPE_IDS = [6, 8, 9, 10, 11];

let keySeed = 0;
export const createBlockItem = (offer: Offer): BlockItem => ({
  key: `new-${Date.now().toString(36)}-${(keySeed++).toString(36)}`,
  offer,
  layoutOverride: null,
  containerOverride: null,
});

const parseJson = (value: any) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

/** What the classic editor's query `select` does: some criteria columns arrive as JSON strings. */
function parseOfferColumns(offer: any) {
  if (!offer) return offer;
  const parsed = { ...offer };
  parsed.criteriaUserAttributes = parseJson(offer.criteriaUserAttributes);
  parsed.suppressionRules = parseJson(offer.suppressionRules);
  parsed.criteriaDayHourRestrictions = offer.criteriaDayHourRestrictions
    ? Object.entries(parseJson(offer.criteriaDayHourRestrictions) ?? {}).map(
        ([restrictionDay, properties]: [string, any]) => ({
          restrictionDay,
          ...properties,
        })
      )
    : [];
  return parsed;
}

/** The classic editor fills these in when missing, so its criteria helpers always find a shape. */
function withConfigurationDefaults(offer: any) {
  const next = { ...offer };
  next.previousRegistrationConfiguration ??= {
    andOrLogic: null,
    previousRegistrations: [
      { domains: [], includeExcludeAction: null, previousRegistrationsMin: null, previousRegistrationsMax: null },
    ],
  };
  next.capSegmentConfiguration ??= { capSegments: [{ domains: [], segmentAmount: null }] };
  next.internalPrePingConfiguration ??= { andOrLogic: 'AND', internalPrePings: [{}] };
  next.domainAffiliateConfiguration ??= { domainAffiliateEntries: [] };
  return next;
}

const idOf = (value: any) => (value && typeof value === 'object' ? value.id : value);

export type LoadedOfferBlock = {
  values: Record<string, any>;
  /** The saved per-offer rows of `offersPublished.offer_block`, by offer id; re-sent merged on save. */
  savedEntries: Record<number, Record<string, any>>;
  block: Record<string, any>;
};

export function loadOfferBlock(block: any): LoadedOfferBlock {
  const offer = withConfigurationDefaults(parseOfferColumns(block?.offer) ?? {});
  const published = Array.isArray(block?.offersPublished?.offer_block) ? block.offersPublished.offer_block : [];
  const savedEntries: Record<number, Record<string, any>> = {};
  published.forEach((entry: any) => {
    if (entry?.offer_id != null && !(entry.offer_id in savedEntries)) savedEntries[entry.offer_id] = entry;
  });

  const stack: BlockItem[] = [...(block?.offerBlockOffers ?? [])]
    .sort((a: any, b: any) => a.orderBy - b.orderBy)
    .filter((row: any) => row.offer != null)
    .map((row: any, i: number) => {
      const saved = savedEntries[row.offer.id];
      const layoutId = idOf(saved?.layout_id);
      const containerId = idOf(saved?.container_id);
      return {
        key: `obo-${row.id ?? i}-${i}`,
        offer: row.offer,
        layoutOverride: layoutId != null && layoutId !== '' ? { id: Number(layoutId) } : null,
        containerOverride: containerId != null && containerId !== '' ? { id: Number(containerId) } : null,
      };
    });

  return {
    values: {
      offer,
      criteria: { criteriaConfiguration: JSON.stringify(block?.criteriaConfiguration ?? {}) },
      stack,
    },
    savedEntries,
    block: block ?? {},
  };
}

/** A new block, as the classic editor's `create` route starts it. */
export const emptyOfferBlock = () => ({
  active: true,
  name: '',
  offer_block_checkout_status: false,
  offerBlockOffers: [],
  offersPublished: { offer_block: [] },
  published: true,
});

/** The classic editor's cleanup of each row: no null or empty values, entities reduced to their ids. */
const compactEntry = (entry: Record<string, any>) => {
  const out: Record<string, any> = {};
  Object.entries(entry).forEach(([key, value]) => {
    if (value === null || value === '') return;
    out[key] =
      value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'id') ? value.id : value;
  });
  return out;
};

export function buildOfferBlockEntries(stack: BlockItem[], savedEntries: LoadedOfferBlock['savedEntries']) {
  // An offer placed twice shares one row of overrides, keyed by offer id, as in the classic editor.
  const overrides = new Map<number, { layout_id?: number; container_id?: number }>();
  stack.forEach((item) => {
    const current = overrides.get(item.offer.id) ?? {};
    if (current.layout_id == null && item.layoutOverride) current.layout_id = item.layoutOverride.id;
    if (current.container_id == null && item.containerOverride) current.container_id = item.containerOverride.id;
    overrides.set(item.offer.id, current);
  });
  return stack.map((item) => {
    const base = { offer_id: item.offer.id, container_id: null, layout_id: null, status: item.offer.isActive ? 1 : 0 };
    const override = overrides.get(item.offer.id) ?? {};
    return compactEntry({
      ...base,
      ...(savedEntries[item.offer.id] ?? {}),
      layout_id: override.layout_id ?? null,
      container_id: override.container_id ?? null,
    });
  });
}

export type OfferBlockSaveContext = {
  offerBlockId: number | null;
  /** Present when saving as a copy: the copy's name. */
  copyName?: string | null;
  /** The live Active state; the toggle saves it on its own, and a publish must not undo that. */
  isActive: boolean;
  savedEntries: LoadedOfferBlock['savedEntries'];
  zuulUser: unknown;
  now?: Date;
};

/** The two requests a publish makes: the block's offer (its name and criteria columns), then the block. */
export function buildOfferBlockSave(values: Record<string, any>, ctx: OfferBlockSaveContext) {
  const now = ctx.now ?? new Date();
  // Required on every path: it re-serialises the criteria columns parsed on load. Sending them parsed
  // makes PUT /offers fail with a 400, as the classic editor's comment records.
  let offer: Record<string, any> = { ...values.offer, ...handleCriteriaSubmit(values.offer) };
  const criteriaConfiguration = values.criteria?.criteriaConfiguration
    ? normalizeCriteriaConfiguration(JSON.parse(values.criteria.criteriaConfiguration))
    : undefined;
  const isCreate = ctx.offerBlockId == null || ctx.copyName != null;

  if (isCreate) {
    offer = { ...offer, isActive: true, zuulUser: ctx.zuulUser };
    delete offer.id;
    if (ctx.copyName) offer.name = ctx.copyName;
  } else {
    offer.isActive = ctx.isActive;
  }

  const block: Record<string, any> = {
    active: true,
    offersPublished: { offer_block: buildOfferBlockEntries(values.stack ?? [], ctx.savedEntries) },
    offer,
    ...(criteriaConfiguration ? { criteriaConfiguration } : {}),
    published: true,
    publishedAt: now,
    zuulUser: ctx.zuulUser,
  };
  if (isCreate) block.createdAt = now;
  else block.id = ctx.offerBlockId;
  return { offer, block, isCreate };
}
